/* eslint complexity: ["error", 7] */
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {FormInput} from '@homeaway/react-form-components';
import {createTextMaskInputElement} from 'text-mask-core';
import classNames from 'classnames';
import './PhoneField.less';

class PhoneField extends Component {
    static propTypes = {
        masks: PropTypes.arrayOf(PropTypes.string),
        onChange: PropTypes.func,
        onFocus: PropTypes.func,
        onBlur: PropTypes.func,
        phoneCode: PropTypes.string,
        phoneNumber: PropTypes.string,
        errorMsg: PropTypes.string
    }

    static defaultProps = {
        labels: {},
        masks: [],
        onChange: () => { },
        onFocus: () => { },
        onBlur: () => { },
        phoneCode: '1',
        phoneNumber: ''
    }

    constructor(props) {
        super(props);

        // initially set to 1 so that the if statement in this.handleChange will run, and we can safely assume that the length of the longest mask will be greater than 0
        this.maskMaxCount = 1;

        this.handleChange = this.handleChange.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleNonNumericalInput = this.handleNonNumericalInput.bind(this);
    }

    componentDidMount() {
        this.initTextMask();
    }

    componentWillReceiveProps(nextProps) {
        const currentValue = this.telField && this.telField.input.value ? this.rawUserInput(this.telField.input.value) : '';

        if (nextProps.phoneNumber !== currentValue) {
            this.textMaskConfig.mask = this.findMasks(nextProps.masks, nextProps.phoneNumber);
            this.textMaskInput.update(nextProps.phoneNumber, this.textMaskConfig);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.phoneCode !== this.props.phoneCode) {
            this.updateMask();
        }
    }

    // polyfill for Array.prototype.find()
    /* eslint-disable consistent-return */
    find(array, predicate, context) {
        context = context || this;
        let length = array.length;
        let i;

        for (i = 0; i < length; i++) {
            if (predicate.call(context, array[i], i, array)) {
                return array[i];
            }
        }
    }
    /* eslint-enable */

    findMasks(maskArray, userInput) {
        const userInputLength = userInput.length;

        // Determine how many input places the user can actually fill in
        // (i.e. all # or digit places minus formatting characters)
        // then sort by length of user input places.
        let sortedArray = maskArray.map((mask) => {
            let charArray = mask.split('');
            let count = 0;
            charArray.forEach((char) => {
                if (char === '#' || (/\d/).test(char)) {
                    count++;
                }
            });
            return {mask, count};
        }).sort((a, b) => a.count - b.count);

        // sets the length of the longest mask to check against later to prevent bug where user can input one more number than should be allowed
        this.maskMaxCount = sortedArray[sortedArray.length - 1].count;

        // if any of the masks have fixed numbers in them and the user DOESN'T
        // start with those numbers, those masks come out of play
        // checks to make sure the fixed digit is not equal to the beginning of
        // the user input && the user has actually started putting in numbers
        let containsFixedDigits = sortedArray.some((obj) => {
            return (/\d/).test(obj.mask);
        });

        // if at least one of the masks contains fixed digits and the user has
        // started typing
        if (containsFixedDigits && userInput.length) {
            // then sort through them all
            sortedArray.forEach((obj, i) => {
                // if this particular mask contains a digit but doesnt match the
                // users input
                if ((/\d/).test(obj.mask) && obj.mask[obj.mask.search(/\d/)] !== userInput[0]) {
                    // splice it out of the available options
                    sortedArray.splice(i, 1);
                }
            });
        }

        const selectedMask = this.find(sortedArray, (mask) => mask.count >= userInputLength) || sortedArray[sortedArray.length - 1];

        return this.createInputMask(selectedMask.mask);
    }

    /** Can be called to externally focus the proper input component. */
    focus() {
        this.telField.focus();
    }

    createInputMask(mask) {
        let maskCharArray = mask.split('');
        let formattedMask = [];

        // eslint-disable-next-line
        maskCharArray.forEach((char) => {
            if (char === '#' || (/\d/).test(char)) {
                formattedMask.push(/\d/);
            }
            if (char === ' ' || char === '(' || char === ')' || char === '-' || char === '.') {
                formattedMask.push(char);
            }
        });
        return formattedMask;
    }

    initTextMask() {
        this.textMaskConfig = {
            inputElement: this.telField.input,
            placeholderChar: '_',
            guide: true,
            showMask: false
        };

        this.textMaskInput = createTextMaskInputElement(this.textMaskConfig);
        this.updateMask();
    }

    rawUserInput(userInput = '') {
        return userInput.split('').filter((c) => ((/\d/).test(c))).join('');
    }

    /**
     * Generates an object containing the current state of the phone field.
     * @returns {{raw: string, formatted: string, phoneCode: string, isValid: boolean}}
     *  An object describing current state: raw is the unformatted phone number,
     *  formatted is the formatted version, phoneCode is the phone country code
     *  (e.g. '+1'), and isValid is true if the number is valid.
     */
    getPhoneObject() {
        return {
            raw: this.rawUserInput(this.telField && this.telField.input.value),
            formatted: this.telField && this.telField.input.value,
            phoneCode: this.props.phoneCode,
            isValid: this.isPhoneNumberValid()
        };
    }

    updateMask(e = {}, allowEmpty) {
        const value = this.telField && (this.telField.input.value || allowEmpty) ? this.telField.input.value : this.props.phoneNumber;
        const userInput = this.rawUserInput(value);
        if (value) {
            this.textMaskConfig.mask = this.findMasks(this.props.masks, userInput);
            this.textMaskInput.update(userInput, this.textMaskConfig);
        }
        this.props.onChange(e, this.getPhoneObject());
    }

    handleChange(e) {
        let userInput = this.rawUserInput(this.telField.input.value);

        // the 'or' here handles the case where a user uses the browser's autofill feature to populate their phone number
        if (userInput.length <= this.maskMaxCount || userInput && !this.props.phoneNumber) {
            this.updateMask(e, true);
        } else {
            this.textMaskInput.update(userInput, this.textMaskConfig);
        }
    }

    handleFocus(e) {
        this.props.onFocus(e, this.getPhoneObject());
    }

    handleBlur(e) {
        this.props.onBlur(e, this.getPhoneObject());
    }

    isPhoneNumberValid() {
        return !(/_/).test(this.telField.input.value);
    }

    isNumberKey = (e) => {
        return (e.which >= 48 && e.which <= 57) || (e.which >= 96 && e.which <= 105);
    }

    isAllowedNonNumericKey = (e) => {
        const isArrow = e.which >= 37 && e.which <= 40;
        const isTabOrDelete = e.which === 8 || e.which === 9 || e.which === 127 || e.which === 46;

        return isArrow || isTabOrDelete;
    }
    /* eslint-disable-next-line complexity */
    isAllowedCombination = (e) => {
        const isCtrl = !!(e.ctrlKey || e.metaKey);
        const isSelectAll = isCtrl && e.which === 65;
        const isCopy = isCtrl && e.which === 67;
        const isPaste = isCtrl && e.which === 86;
        const isCut = isCtrl && e.which === 88;
        const isUndo = isCtrl && e.which === 90;
        const isRedo = isCtrl && e.which === 89;

        return isSelectAll || isCopy || isPaste || isCut || isUndo || isRedo;
    }

    // ignore any key other than 0-9, (arrows), backspace, tab, delete, ctrl + a, cmd + a
    handleNonNumericalInput(e = {}) {
        if (!this.isNumberKey(e) && !this.isAllowedNonNumericKey(e) && !this.isAllowedCombination(e)) {
            e.preventDefault();
        }
    }

    render() {
        const {
            id,
            labels,
            phoneCode,
            showCountrySelect,
            phoneNumber,
            errorMsg
        } = this.props;

        const inputClassName = classNames({
            [`react-input-tel__country-code-${phoneCode.length}`]: true,
            'react-input-tel__phone-field': true,
            'show-dropdown': showCountrySelect,
            'not-empty': phoneNumber || (this.telField && this.telField.input.value)
        });

        return (
            <div
                role="presentation"
                className="input-group-wrapper"
                onKeyDown={this.handleNonNumericalInput}
            >
                <FormInput
                    ref={(el) => {
                        /* istanbul ignore next */
                        this.telField = el;
                    }}
                    id={id}
                    data-wdio="input-tel__phone-field__input"
                    className={inputClassName}
                    leftContent={(!showCountrySelect) ? `+${phoneCode}` : null}
                    label={labels.phoneNumber}
                    errorMsg={errorMsg}
                    onChange={this.handleChange}
                    onFocus={this.handleFocus}
                    onBlur={this.handleBlur}
                    type="tel"
                    autoComplete="tel"
                />
            </div>
        );
    }
}

export default PhoneField;
